import React, { useState } from "react";
import {
  Box,
  Modal,
  Typography,
  IconButton,
  Button,
  InputAdornment,
  OutlinedInput,
  FormControl,
  Switch,
  FormControlLabel,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LockIcon from "@mui/icons-material/Lock";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckIcon from "@mui/icons-material/Check";
import { updatePassword } from "../../utils/api";

const SecuritySettingsModal = ({ open, handleClose }) => {
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [twoFA, setTwoFA] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const toggleCurrent = () => setShowCurrent((prev) => !prev);
  const toggleNew = () => setShowNew((prev) => !prev);
  const toggleConfirm = () => setShowConfirm((prev) => !prev);

  // Simple password strength indicator
  const getPasswordStrength = () => {
    if (!newPassword) return "";
    if (newPassword.length < 6) return "Weak";
    if (newPassword.length < 10) return "Medium";
    return "Strong";
  };

  const handleSave = async () => {
    setError("");
    setSuccess("");

    if (newPassword !== confirmPassword) {
      setError("New password and confirmation do not match");
      return;
    }

    try {
      await updatePassword(currentPassword, newPassword, confirmPassword);
      setSuccess("Password updated successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      // Optionally close modal after a short delay
      setTimeout(() => handleClose(), 1500);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || "Failed to update password");
    }

    // TODO: Save 2FA preference to backend once implemented
    console.log("2FA enabled:", twoFA);
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          borderRadius: 3,
          boxShadow: 24,
          p: 3,
          width: "100%",
          maxWidth: "400px",
          minWidth: 300,
          mx: "auto",
        }}
      >
        {/* Close Icon */}
        <IconButton
          onClick={handleClose}
          sx={{ position: "absolute", top: 10, right: 10, zIndex: 1 }}
        >
          <CloseIcon />
        </IconButton>

        {/* Title */}
        <Typography variant="h6" fontWeight="bold" mb={3}>
          Security Settings
        </Typography>

        {/* Error / Success Alerts */}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {/* Current Password */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <OutlinedInput
            type={showCurrent ? "text" : "password"}
            placeholder="Current Password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            startAdornment={
              <InputAdornment position="start">
                <LockIcon color="disabled" />
              </InputAdornment>
            }
            endAdornment={
              <InputAdornment position="end">
                <IconButton onClick={toggleCurrent} edge="end">
                  {showCurrent ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />
        </FormControl>

        {/* New Password */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <OutlinedInput
            type={showNew ? "text" : "password"}
            placeholder="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            startAdornment={
              <InputAdornment position="start">
                <LockIcon color="disabled" />
              </InputAdornment>
            }
            endAdornment={
              <InputAdornment position="end">
                <IconButton onClick={toggleNew} edge="end">
                  {showNew ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />
          {newPassword && (
            <Typography variant="caption" sx={{ mt: 0.5 }}>
              Password strength: <strong>{getPasswordStrength()}</strong>
            </Typography>
          )}
        </FormControl>

        {/* Confirm New Password */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <OutlinedInput
            type={showConfirm ? "text" : "password"}
            placeholder="Confirm New Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            startAdornment={
              <InputAdornment position="start">
                <CheckIcon color="disabled" />
              </InputAdornment>
            }
            endAdornment={
              <InputAdornment position="end">
                <IconButton onClick={toggleConfirm} edge="end">
                  {showConfirm ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />
        </FormControl>

        {/* 2FA Toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={twoFA}
              onChange={(e) => setTwoFA(e.target.checked)}
              color="primary"
            />
          }
          label="Enable Two-Factor Authentication"
          sx={{ mb: 2 }}
        />

        {/* Security Alert */}
        <Alert severity="warning" sx={{ mb: 2, fontSize: "0.85rem" }}>
          Keep your password confidential. Never share your credentials with anyone, including support staff.
        </Alert>

        {/* Save Button */}
        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={handleSave}
        >
          Save Changes
        </Button>
      </Box>
    </Modal>
  );
};

export default SecuritySettingsModal;